/**
 * Dev-only Firestore listener instrumentation for Studio refactor baseline.
 * Tracks active listener keys so duplicate subscription paths are visible in the console.
 *
 * Baseline duplicate paths removed/consolidated in Phase 1:
 * - StudioStatsBar N+1 capture listeners → StudioMetricsProvider (single studio + per-project set)
 * - billing/metrics + billing/plan listeners → StudioMetricsProvider
 * - countProcessingCapturesAcrossStudio / countCompletedCapturesAcrossStudio getDocs scans
 *   → derived from live listener cache when StudioMetricsProvider is mounted
 */

const listeners = new Map();

function isEnabled() {
  return process.env.NODE_ENV === 'development';
}

/** @param {string} key Stable listener id (e.g. "studioMetrics:captures:projId") */
export function registerStudioFirestoreListener(key) {
  if (!isEnabled()) return () => {};
  const prev = listeners.get(key) ?? 0;
  listeners.set(key, prev + 1);
  if (prev === 0) {
    // eslint-disable-next-line no-console
    console.debug('[studioFirestore] + listener', key, `(active: ${listeners.size})`);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const n = listeners.get(key) ?? 0;
    if (n <= 1) {
      listeners.delete(key);
      // eslint-disable-next-line no-console
      console.debug('[studioFirestore] - listener', key, `(active: ${listeners.size})`);
    } else {
      listeners.set(key, n - 1);
    }
  };
}

/** @returns {string[]} Active listener keys (dev diagnostics). */
export function getActiveStudioFirestoreListeners() {
  return [...listeners.keys()];
}

/** @returns {Record<string, number>} Listener key → ref count. */
export function getStudioFirestoreListenerSnapshot() {
  return Object.fromEntries(listeners);
}
